import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coachAPI, sceneAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import CoachLayout from './CoachLayout';
import AccessDenied from '../../components/AccessDenied';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function TrackModal({ domain, track, onSave, onClose }) {
  const [displayName, setDisplayName] = useState(track?.display_name || '');
  const [description, setDescription] = useState(track?.description || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    try {
      if (track) {
        await coachAPI.updateTrack(domain, track.track_id, { display_name: displayName, description });
      } else {
        await coachAPI.createTrack(domain, { display_name: displayName, description });
      }
      onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">{track ? 'Edit Track' : 'New Track'}</h3>
        <div className="coach-form-group">
          <label className="coach-label">Track Name</label>
          <input
            className="coach-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Coverage Recognition"
          />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Description</label>
          <textarea
            className="coach-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief overview of this track…"
          />
        </div>
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : track ? 'Update Track' : 'Create Track'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Multi-select chip picker — shows real names, not IDs */
function ChipPicker({ label, hint, items, selected, onToggle, loading }) {
  const selectedCount = selected.length;
  return (
    <div className="coach-form-group" style={{ gridColumn: '1/-1' }}>
      <label className="coach-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {label}
        {selectedCount > 0 && (
          <span style={{
            background: 'rgba(99,102,241,0.25)',
            color: '#a78bfa',
            borderRadius: 9999,
            padding: '1px 8px',
            fontSize: '0.75rem',
            fontWeight: 700,
          }}>{selectedCount} selected</span>
        )}
      </label>
      <p className="text-muted text-sm" style={{ marginTop: 2, marginBottom: 8 }}>{hint}</p>
      {loading ? (
        <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>None available — add some first.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((item) => {
            const id = item.id;
            const isSelected = selected.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => onToggle(id)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 9999,
                  border: `2px solid ${isSelected ? '#6366f1' : 'rgba(255,255,255,0.15)'}`,
                  background: isSelected ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                  color: isSelected ? '#a78bfa' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: isSelected ? 700 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {isSelected ? '✓ ' : ''}{item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * OrderedPicker — shows a numbered, reorderable list of selected items
 * plus an "available" pool to pick from. Used for Scenes and Lessons
 * where the array order determines the learner's content sequence.
 *
 * Props:
 *   label, hint  — section heading and sub-text
 *   items        — all available options: [{ id, label }]
 *   selected     — ordered array of selected IDs
 *   onChange(newOrderedIds) — replace the full ordered array
 *   loading      — show loading state while options are being fetched
 */
/**
 * OrderedPicker — numbered, reorderable list with optional per-item edit link.
 * onEditItem(id) — if provided, renders a pencil button that calls it.
 */
function OrderedPicker({ label, hint, items, selected, onChange, loading, onEditItem }) {
  const itemMap = Object.fromEntries(items.map((i) => [i.id, i.label]));
  const available = items.filter((i) => !selected.includes(i.id));

  const move = (idx, dir) => {
    const next = [...selected];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const add = (id) => onChange([...selected, id]);
  const remove = (id) => onChange(selected.filter((x) => x !== id));

  return (
    <div className="coach-form-group" style={{ gridColumn: '1/-1' }}>
      <label className="coach-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {label}
        {selected.length > 0 && (
          <span style={{
            background: 'rgba(99,102,241,0.25)',
            color: '#a78bfa',
            borderRadius: 9999,
            padding: '1px 8px',
            fontSize: '0.75rem',
            fontWeight: 700,
          }}>{selected.length} selected</span>
        )}
      </label>
      <p className="text-muted text-sm" style={{ marginTop: 2, marginBottom: 8 }}>{hint}</p>

      {loading ? (
        <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>Loading…</div>
      ) : (
        <>
          {/* Selected items — ordered list */}
          {selected.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {selected.map((id, idx) => (
                <div key={id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(99,102,241,0.12)',
                  border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 6, padding: '4px 10px',
                }}>
                  <span style={{ color: '#64748b', fontSize: '0.75rem', minWidth: 20, textAlign: 'center' }}>
                    {idx + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: '#e2e8f0' }}>
                    {itemMap[id] || id}
                  </span>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                    style={{ background: 'none', border: 'none', color: idx === 0 ? '#334155' : '#94a3b8', cursor: idx === 0 ? 'default' : 'pointer', padding: '0 4px', fontSize: '0.8rem' }}>
                    ↑
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === selected.length - 1}
                    style={{ background: 'none', border: 'none', color: idx === selected.length - 1 ? '#334155' : '#94a3b8', cursor: idx === selected.length - 1 ? 'default' : 'pointer', padding: '0 4px', fontSize: '0.8rem' }}>
                    ↓
                  </button>
                  <button type="button" onClick={() => remove(id)}
                    style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0 4px', fontSize: '0.8rem' }}>
                    ×
                  </button>
                  {onEditItem && (
                    <button type="button" onClick={() => onEditItem(id)}
                      title="Edit this lesson"
                      style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: '0 4px', fontSize: '0.8rem' }}>
                      ✎
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Available pool */}
          {available.length > 0 ? (
            <div>
              <div className="text-muted text-sm" style={{ marginBottom: 6, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Add to list
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {available.map((item) => (
                  <button key={item.id} type="button" onClick={() => add(item.id)}
                    style={{
                      padding: '3px 10px', borderRadius: 9999,
                      border: '1px dashed rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.04)',
                      color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem',
                    }}>
                    + {item.label}
                  </button>
                ))}
              </div>
            </div>
          ) : selected.length === 0 ? (
            <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>None available — add some from the Content Library first.</div>
          ) : null}
        </>
      )}
    </div>
  );
}


// Domains that use football-specific metadata (plays, coverages).
const FOOTBALL_DOMAINS = new Set(['football', 'ai_beginner']);

function ModuleModal({ domain, trackId, module, allModules, onSave, onClose }) {
  const navigate = useNavigate();
  const isFootball = FOOTBALL_DOMAINS.has(domain);

  const [form, setForm] = useState({
    display_name: module?.display_name || '',
    description: module?.description || '',
    order: module?.order ?? (allModules.length + 1),
    difficulty: module?.difficulty || 'beginner',
    mastery_gate_accuracy: module?.mastery_gate?.required_accuracy ?? 0.75,
    mastery_gate_attempts: module?.mastery_gate?.required_attempts ?? 3,
  });
  const [selectedCoverages, setSelectedCoverages] = useState(module?.coverage_catalog_ids || []);
  const [selectedPlays, setSelectedPlays] = useState(module?.play_ids || []);
  const [selectedPrereqs, setSelectedPrereqs] = useState(module?.prerequisites || []);
  const [selectedTutorials, setSelectedTutorials] = useState(module?.tutorial_ids || []);
  const [selectedScenarios, setSelectedScenarios] = useState(module?.scenario_ids || []);
  // target_roles: ["*"] = all, or subset of ["learner","coach_learner"]
  const [targetRoles, setTargetRoles] = useState(() => {
    const saved = module?.target_roles;
    if (!saved || saved.length === 0) return ['*'];
    return saved;
  });

  const [coverageOptions, setCoverageOptions] = useState([]);
  const [playOptions, setPlayOptions] = useState([]);
  const [tutorialOptions, setTutorialOptions] = useState([]);
  const [sceneOptions, setSceneOptions] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load content options. All domains fetch lessons; football additionally
  // fetches plays and coverages as supplementary metadata.
  useEffect(() => {
    setLoadingContent(true);
    const fetches = [
      coachAPI.listTutorials({ domain_id: domain }).then(({ data }) => {
        setTutorialOptions((data.tutorials || []).map((t) => ({
          id: t.tutorial_id,
          label: t.title || t.tutorial_id,
        })));
      }).catch(() => {}),
      sceneAPI.list({ domain_id: domain }).then(({ data }) => {
        const CONTENT_TYPE_LABELS = {
          video: 'Video', slide_deck: 'Slides', content_panel: 'Reading',
          interactive_quiz: 'Quiz', quiz_panel: 'Quiz', reflection: 'Reflection',
          ai_lab: 'AI Lab', curated_resource: 'Resource', prompt_canvas: 'Prompt Canvas',
          code_lab: 'Code Lab', capstone: 'Capstone', module_assessment: 'Assessment',
        };
        setSceneOptions((data.scenes || []).map((s) => {
          const ct = s.domain_metadata?.content_type || s.scene_type || '';
          const badge = CONTENT_TYPE_LABELS[ct] || ct.replace(/_/g, ' ');
          return {
            id: s.scene_id,
            label: `${s.display_name || s.scene_id} [${badge}]`,
          };
        }));
      }).catch(() => {}),
    ];
    if (isFootball) {
      fetches.push(
        coachAPI.listCoverages(domain).then(({ data }) => {
          setCoverageOptions((data.coverages || []).map((c) => ({
            id: c.coverage_id,
            label: c.display_name,
          })));
        }).catch(() => {}),
        coachAPI.listPlays(domain).then(({ data }) => {
          setPlayOptions((data.plays || []).map((p) => ({
            id: p.play_id,
            label: p.display_name,
          })));
        }).catch(() => {}),
      );
    }
    Promise.allSettled(fetches).finally(() => setLoadingContent(false));
  }, [domain, isFootball]);

  const toggle = (setter) => (id) =>
    setter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const prereqOptions = allModules
    .filter((m) => m.module_id !== module?.module_id)
    .map((m) => ({ id: m.module_id, label: m.display_name }));

  const handleSave = async () => {
    if (!form.display_name.trim()) return;
    setSaving(true);
    const payload = {
      display_name: form.display_name,
      description: form.description,
      order: Number(form.order),
      difficulty: form.difficulty,
      coverage_catalog_ids: selectedCoverages,
      play_ids: selectedPlays,
      tutorial_ids: selectedTutorials,
      scenario_ids: selectedScenarios,
      prerequisites: selectedPrereqs,
      mastery_gate: {
        required_accuracy: Number(form.mastery_gate_accuracy),
        required_attempts: Number(form.mastery_gate_attempts),
      },
      target_roles: targetRoles,
    };
    try {
      let updated;
      if (module) {
        const { data } = await coachAPI.updateModule(domain, trackId, module.module_id, payload);
        updated = data;
        toast.success('Module updated — changes are live for learners immediately.');
      } else {
        const { data } = await coachAPI.createModule(domain, trackId, payload);
        updated = data;
        toast.success('Module created.');
      }
      onSave(updated);
    } finally {
      setSaving(false);
    }
  };

  const goEditLesson = (tutId) => {
    navigate(`/coach/tutorials/${tutId}`);
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 640, maxHeight: '92vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">{module ? 'Edit Module' : 'New Module'}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="coach-form-group" style={{ gridColumn: '1/-1' }}>
            <label className="coach-label">Module Name</label>
            <input className="coach-input" value={form.display_name} onChange={set('display_name')} placeholder="e.g. Zone Coverage Reads" />
          </div>
          <div className="coach-form-group" style={{ gridColumn: '1/-1' }}>
            <label className="coach-label">Description</label>
            <textarea className="coach-textarea" value={form.description} onChange={set('description')} placeholder="What will learners practice in this module?" />
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Order</label>
            <input className="coach-input" type="number" min="1" value={form.order} onChange={set('order')} />
          </div>
          <div className="coach-form-group">
            <label className="coach-label">
              Difficulty
              <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: 6 }}>
                (shown as a badge on the learner's module card)
              </span>
            </label>
            <select className="coach-select" value={form.difficulty} onChange={set('difficulty')}>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Target Roles — controls who sees this module */}
          <div className="coach-form-group">
            <label className="coach-label">
              Target Roles
              <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: 6 }}>
                (who can see this module)
              </span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {[
                { value: '*', label: 'All roles' },
                { value: 'learner', label: 'Learners only' },
                { value: 'coach_learner', label: 'Coach-Learners only' },
              ].map(({ value, label }) => {
                const isAllRoles = value === '*';
                const checked = isAllRoles
                  ? targetRoles.includes('*')
                  : targetRoles.includes(value) && !targetRoles.includes('*');
                const handleChange = () => {
                  if (isAllRoles) {
                    setTargetRoles(['*']);
                  } else {
                    setTargetRoles((prev) => {
                      const without_star = prev.filter((r) => r !== '*');
                      if (without_star.includes(value)) {
                        const next = without_star.filter((r) => r !== value);
                        return next.length === 0 ? ['*'] : next;
                      }
                      return [...without_star, value];
                    });
                  }
                };
                return (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', color: '#cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={handleChange}
                      style={{ accentColor: '#6366f1' }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Football-only supplementary metadata */}
          {isFootball && (
            <>
              <ChipPicker
                label="Coverages in this module"
                hint="Defensive coverages learners will encounter in simulation steps of this module's lessons."
                items={coverageOptions}
                selected={selectedCoverages}
                onToggle={toggle(setSelectedCoverages)}
                loading={loadingContent}
              />
              <ChipPicker
                label="Plays in this module"
                hint="Offensive plays used as the basis for reading scenarios in simulation steps."
                items={playOptions}
                selected={selectedPlays}
                onToggle={toggle(setSelectedPlays)}
                loading={loadingContent}
              />
            </>
          )}

          {/* Lessons picker — unified for all domains.
              Each lesson contains its own ordered segments (video, slides, quiz,
              simulation, etc.) managed in the Lesson editor. */}
          <OrderedPicker
            label="Lessons in this module"
            hint="Assign lessons to this module. Each lesson is a step-by-step learning experience (video → slides → quiz → practice). Use ↑ ↓ to set the order learners see them."
            items={tutorialOptions}
            selected={selectedTutorials}
            onChange={setSelectedTutorials}
            loading={loadingContent}
            onEditItem={goEditLesson}
          />

          {/* Quick link to create a new lesson for this domain */}
          <div style={{ gridColumn: '1/-1', marginTop: -4 }}>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/coach/tutorials')}
            >
              + Create / manage lessons →
            </button>
          </div>

          {/* Content Scenes picker — shows scenarios assigned to this module.
              Each scenario maps to a segment in the published lesson. */}
          <OrderedPicker
            label="Content Scenes (Scenarios)"
            hint="Scenes that make up this module's content. Each scene becomes a segment in the published lesson. Use ↑ ↓ to control the learner's content flow."
            items={sceneOptions}
            selected={selectedScenarios}
            onChange={setSelectedScenarios}
            loading={loadingContent}
          />

          {/* Prerequisites picker */}
          {prereqOptions.length > 0 && (
            <ChipPicker
              label="Prerequisites"
              hint="Modules the learner must complete before this one unlocks. Leave empty to make it available immediately."
              items={prereqOptions}
              selected={selectedPrereqs}
              onToggle={toggle(setSelectedPrereqs)}
            />
          )}

          {/* Mastery gate */}
          <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--coach-border)', paddingTop: 12, marginTop: 4 }}>
            <div className="coach-label" style={{ marginBottom: 4 }}>Mastery Gate</div>
            <p className="text-muted text-sm" style={{ marginBottom: 10 }}>
              Learners must meet both thresholds across their attempts to mark this module complete and unlock the next one.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="coach-form-group">
                <label className="coach-label">Required Accuracy</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="coach-input"
                    type="range" min="0.5" max="1" step="0.05"
                    value={form.mastery_gate_accuracy}
                    onChange={set('mastery_gate_accuracy')}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 700, color: '#a78bfa' }}>
                    {Math.round(form.mastery_gate_accuracy * 100)}%
                  </span>
                </div>
              </div>
              <div className="coach-form-group">
                <label className="coach-label">Minimum Attempts</label>
                <input className="coach-input" type="number" min="1" max="20" value={form.mastery_gate_attempts} onChange={set('mastery_gate_attempts')} />
                <span className="text-muted text-sm">sessions before mastery can be awarded</span>
              </div>
            </div>
          </div>
        </div>

        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : module ? 'Update Module' : 'Add Module'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Move-module modal — pick a destination track and reassign the module.
 * Backed by PUT /coach/curriculum/{domain}/tracks/{src}/modules/{id} with
 * a `track_id` in the body, which the API uses to swap the module's
 * track and keep both curriculum_tracks.module_ids arrays in sync.
 */
function MoveModuleModal({ domain, sourceTrackId, module, allTracks, onSave, onClose }) {
  const [destTrackId, setDestTrackId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const candidates = (allTracks || []).filter(
    (t) => t.track_id !== sourceTrackId && t.source !== 'yaml'
  );

  const handleMove = async () => {
    if (!destTrackId) return;
    setErr(null);
    setSaving(true);
    try {
      await coachAPI.updateModule(domain, sourceTrackId, module.module_id, {
        track_id: destTrackId,
      });
      onSave();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">Move "{module.display_name}"</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
          Reassign this module to a different curriculum track. The module's
          contents stay intact; only its parent track changes.
        </p>
        {candidates.length === 0 ? (
          <div className="coach-empty" style={{ padding: '12px 0' }}>
            <div className="coach-empty-desc">
              No other writable tracks exist in this domain. Create a new
              track first to give this module somewhere to land.
            </div>
          </div>
        ) : (
          <div className="coach-form-group">
            <label className="coach-label">Destination track</label>
            <select
              className="coach-select"
              value={destTrackId}
              onChange={(e) => setDestTrackId(e.target.value)}
            >
              <option value="">Select a track…</option>
              {candidates.map((t) => (
                <option key={t.track_id} value={t.track_id}>
                  {t.display_name}
                </option>
              ))}
            </select>
          </div>
        )}
        {err && <div className="coach-error" style={{ marginTop: 8 }}>⚠ {err}</div>}
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleMove}
            disabled={saving || !destTrackId || candidates.length === 0}
          >
            {saving ? 'Moving…' : 'Move module'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Coach Push Resource Panel ───────────────────────────────────────────── */

function CoachPushResourcePanel({ domain, onClose }) {
  const [form, setForm] = React.useState({
    resource_url: '',
    title: '',
    source_name: '',
    difficulty: '',
    resource_type: 'curated_resource',
  });
  const [pushing, setPushing] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handlePush = async () => {
    if (!form.resource_url.trim() || !form.title.trim()) {
      setErr('URL and title are required.');
      return;
    }
    setErr('');
    setPushing(true);
    try {
      const token = localStorage.getItem('scenario_os_token');
      const res = await fetch('/scenario-os/curriculum/resources/coach-push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          domain,
          resource_url: form.resource_url.trim(),
          title: form.title.trim(),
          source_name: form.source_name.trim(),
          difficulty: form.difficulty || null,
          resource_type: form.resource_type,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data.pushed_to ?? 0);
    } catch (e) {
      setErr(e.message || 'Push failed.');
    } finally {
      setPushing(false);
    }
  };

  if (result !== null) {
    return (
      <div style={pushPanelStyle}>
        <div style={{ color: '#059669', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
          ✓ Resource pushed to {result} learner{result !== 1 ? 's' : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => { setResult(null); setForm({ resource_url: '', title: '', source_name: '', difficulty: '', resource_type: 'curated_resource' }); }}>Push another</button>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div style={pushPanelStyle}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#e2e8f0' }}>
        📤 Push resource to all learners in <em>{domain}</em>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div className="coach-form-group" style={{ gridColumn: '1/-1', margin: 0 }}>
          <label className="coach-label" style={{ fontSize: '0.78rem' }}>Resource URL *</label>
          <input className="coach-input" value={form.resource_url} onChange={set('resource_url')} placeholder="https://…" />
        </div>
        <div className="coach-form-group" style={{ margin: 0 }}>
          <label className="coach-label" style={{ fontSize: '0.78rem' }}>Title *</label>
          <input className="coach-input" value={form.title} onChange={set('title')} placeholder="Resource title" />
        </div>
        <div className="coach-form-group" style={{ margin: 0 }}>
          <label className="coach-label" style={{ fontSize: '0.78rem' }}>Source</label>
          <input className="coach-input" value={form.source_name} onChange={set('source_name')} placeholder="e.g. YouTube, Article" />
        </div>
        <div className="coach-form-group" style={{ margin: 0 }}>
          <label className="coach-label" style={{ fontSize: '0.78rem' }}>Difficulty</label>
          <select className="coach-select" value={form.difficulty} onChange={set('difficulty')}>
            <option value="">— none —</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div className="coach-form-group" style={{ margin: 0 }}>
          <label className="coach-label" style={{ fontSize: '0.78rem' }}>Type</label>
          <select className="coach-select" value={form.resource_type} onChange={set('resource_type')}>
            <option value="curated_resource">Curated Resource</option>
            <option value="prompt_canvas">Prompt Canvas</option>
          </select>
        </div>
      </div>
      {err && <div className="coach-error" style={{ marginBottom: 8, fontSize: '0.8rem' }}>⚠ {err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={handlePush} disabled={pushing}>
          {pushing ? 'Pushing…' : '📤 Push to all learners'}
        </button>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

const pushPanelStyle = {
  background: 'rgba(0,0,0,0.25)',
  border: '1px solid var(--coach-border)',
  borderRadius: 8,
  padding: 16,
  marginTop: 10,
};

/* ── Main Page ───────────────────────────────────────────────────────────── */

export default function CurriculumBuilderPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const [showTrackModal, setShowTrackModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [movingModule, setMovingModule] = useState(null);
  const [movingFromTrackId, setMovingFromTrackId] = useState(null);
  const [showPushPanel, setShowPushPanel] = useState(false);

  const loadTracks = useCallback(() => {
    setLoading(true);
    // Use tracks-with-import which auto-seeds YAML content to MongoDB on first access
    coachAPI.listTracksWithImport(domain)
      .then(({ data }) => setTracks(data.tracks || []))
      .catch((err) => {
        if (err.response?.status === 403) { setAccessDenied(true); }
        else { setError(err.message); }
      })
      .finally(() => setLoading(false));
  }, [domain]);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const loadModules = (trackId) => {
    coachAPI.listModules(domain, trackId)
      .then(({ data }) => setModules((prev) => ({ ...prev, [trackId]: data.modules || [] })))
      .catch(() => {});
  };

  const toggleTrack = (trackId) => {
    if (expandedTrack === trackId) {
      setExpandedTrack(null);
    } else {
      setExpandedTrack(trackId);
      if (!modules[trackId]) loadModules(trackId);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    const ok = await confirm({
      title: 'Delete track?',
      message: 'Delete this track and all its modules? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deleteTrack(domain, trackId).catch(() => {});
    toast.info('Track deleted');
    loadTracks();
  };

  const handleDeleteModule = async (trackId, moduleId) => {
    const ok = await confirm({
      title: 'Delete module?',
      message: 'Delete this module? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deleteModule(domain, trackId, moduleId).catch(() => {});
    toast.info('Module deleted');
    loadModules(trackId);
  };

  const openNewTrack = () => { setEditingTrack(null); setShowTrackModal(true); };
  const openEditTrack = (track) => { setEditingTrack(track); setShowTrackModal(true); };
  const openNewModule = (trackId) => { setActiveTrackId(trackId); setEditingModule(null); setShowModuleModal(true); };
  const openEditModule = (trackId, mod) => { setActiveTrackId(trackId); setEditingModule(mod); setShowModuleModal(true); };
  const openMoveModule = (trackId, mod) => { setMovingFromTrackId(trackId); setMovingModule(mod); };

  if (accessDenied) return <AccessDenied domainId={domain} onBack={() => navigate('/coach')} />;

  return (
    <CoachLayout title={`Curriculum Builder — ${domain.charAt(0).toUpperCase() + domain.slice(1)}`}>
      {error && <div className="coach-error">⚠ {error}</div>}

      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Curriculum Tracks</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setShowPushPanel((v) => !v)}>
              📤 Push resource to all learners
            </button>
            <button className="btn btn-primary" onClick={openNewTrack}>+ New Track</button>
          </div>
        </div>

        {showPushPanel && (
          <CoachPushResourcePanel domain={domain} onClose={() => setShowPushPanel(false)} />
        )}

        {loading ? (
          <div className="coach-loading">Loading curriculum…</div>
        ) : tracks.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">🏗️</span>
            <div className="coach-empty-title">No tracks yet</div>
            <div className="coach-empty-desc">Create your first curriculum track to organise modules.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tracks.map((track) => (
              <div key={track.track_id} style={{
                border: '1px solid var(--coach-border)',
                borderRadius: 'var(--coach-radius)',
                overflow: 'hidden',
              }}>
                {/* Track header */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 18px', background: 'var(--coach-card-bg)',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleTrack(track.track_id)}
                >
                  <span style={{ fontSize: '1.1rem' }}>{expandedTrack === track.track_id ? '▾' : '▸'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{track.display_name}</div>
                    {track.description && (
                      <div className="text-muted text-sm" style={{ marginTop: 2 }}>{track.description}</div>
                    )}
                    {track.source === 'yaml' && (
                      <span className="badge badge-draft" style={{ marginTop: 4 }}>YAML (read-only)</span>
                    )}
                  </div>
                  <div className="flex-row" onClick={(e) => e.stopPropagation()}>
                    {track.source !== 'yaml' && (
                      <>
                        <button className="btn btn-outline btn-sm" onClick={() => openEditTrack(track)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTrack(track.track_id)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>

                {/* Modules panel */}
                {expandedTrack === track.track_id && (
                  <div style={{ padding: '0 18px 16px', background: 'rgba(0,0,0,0.15)' }}>
                    <div className="flex-between" style={{ padding: '14px 0 10px' }}>
                      <span className="coach-label">Modules</span>
                      {track.source !== 'yaml' && (
                        <button className="btn btn-primary btn-sm" onClick={() => openNewModule(track.track_id)}>
                          + Add Module
                        </button>
                      )}
                    </div>

                    {!modules[track.track_id] ? (
                      <div className="coach-loading" style={{ padding: 16 }}>Loading modules…</div>
                    ) : modules[track.track_id].length === 0 ? (
                      <div className="coach-empty" style={{ padding: '16px 0' }}>
                        <div className="coach-empty-desc">No modules yet. Add one to get started.</div>
                      </div>
                    ) : (
                      <table className="coach-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Module Name</th>
                            <th>Difficulty</th>
                            <th>Lessons</th>
                            <th>Mastery Gate</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {modules[track.track_id].map((m) => (
                            <tr key={m.module_id}>
                              <td className="text-muted text-sm">{m.order}</td>
                              <td style={{ fontWeight: 600 }}>{m.display_name}</td>
                              <td><span className={`badge badge-${m.difficulty}`}>{m.difficulty}</span></td>
                              <td className="text-muted text-sm">
                                {(m.tutorial_ids || []).length > 0
                                  ? (m.tutorial_ids || []).length
                                  : <span style={{ color: '#f59e0b', fontSize: '0.78rem' }}>None assigned</span>
                                }
                              </td>
                              <td className="text-muted text-sm">
                                {m.mastery_gate
                                  ? `${Math.round((m.mastery_gate.required_accuracy || 0.75) * 100)}% / ${m.mastery_gate.required_attempts || 3} attempts`
                                  : '—'}
                              </td>
                              <td>
                                {m.source !== 'yaml' && (
                                  <div className="flex-row">
                                    <button className="btn btn-outline btn-sm" onClick={() => openEditModule(track.track_id, m)}>Edit</button>
                                    <button
                                      className="btn btn-outline btn-sm"
                                      onClick={() => openMoveModule(track.track_id, m)}
                                      title="Move this module to a different track"
                                    >
                                      Move…
                                    </button>
                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteModule(track.track_id, m.module_id)}>Del</button>
                                  </div>
                                )}
                                {m.source === 'yaml' && <span className="badge badge-draft">YAML</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showTrackModal && (
        <TrackModal
          domain={domain}
          track={editingTrack}
          onSave={() => { setShowTrackModal(false); loadTracks(); }}
          onClose={() => setShowTrackModal(false)}
        />
      )}

      {showModuleModal && (
        <ModuleModal
          domain={domain}
          trackId={activeTrackId}
          module={editingModule}
          allModules={modules[activeTrackId] || []}
          onSave={(updatedModule) => {
            setShowModuleModal(false);
            // Immediately patch the in-memory state so reopening the
            // modal reflects the save without waiting for a round-trip.
            if (updatedModule) {
              setModules((prev) => {
                const list = prev[activeTrackId] || [];
                const exists = list.some((m) => m.module_id === updatedModule.module_id);
                const next = exists
                  ? list.map((m) => m.module_id === updatedModule.module_id ? updatedModule : m)
                  : [...list, updatedModule];
                return { ...prev, [activeTrackId]: next };
              });
            }
            // Background refresh to catch any server-side normalisation.
            loadModules(activeTrackId);
          }}
          onClose={() => setShowModuleModal(false)}
        />
      )}

      {movingModule && (
        <MoveModuleModal
          domain={domain}
          sourceTrackId={movingFromTrackId}
          module={movingModule}
          allTracks={tracks}
          onSave={() => {
            const src = movingFromTrackId;
            setMovingModule(null);
            setMovingFromTrackId(null);
            // Refresh source so the moved module disappears, plus the
            // tracks list so module counts (if any) re-render. Destination
            // refreshes lazily when the coach expands it next.
            loadModules(src);
            loadTracks();
          }}
          onClose={() => { setMovingModule(null); setMovingFromTrackId(null); }}
        />
      )}
    </CoachLayout>
  );
}
